import { Link } from 'react-router-dom';
import { Clock, Users } from 'lucide-react';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1580541631950-7282082b53ce?w=800';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export const TourCard = ({ tour, index = 0 }) => {
  const mainImage = getImageUrl(tour.images?.[0]);
  const { t } = useLanguage();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.23, 1, 0.32, 1] }}
    >
      <Link 
        to={`/tour/${tour.slug}`}
        className="block group"
        data-testid={`tour-card-${tour.slug}`}
      >
        <div className="tropical-card h-full">
          {/* Image Container */}
          <div className="relative h-64 md:h-72 overflow-hidden">
            <motion.img
              src={mainImage}
              alt={tour.name}
              className="w-full h-full object-cover"
              loading="lazy"
              whileHover={{ scale: 1.08 }}
              transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
            />
            
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#012A36]/80 via-transparent to-transparent" />
            
            {/* Tags - Glass Style */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              {tour.tags?.includes('Más vendido') && (
                <Badge className="glass text-[#012A36] text-xs font-semibold px-3 py-1.5 rounded-full">
                  {t('tours_best_seller')}
                </Badge>
              )}
              {tour.tags?.includes('Nuevo') && (
                <Badge className="bg-[#00A896] text-white text-xs font-semibold px-3 py-1.5 rounded-full border-0">
                  {t('tours_new')}
                </Badge>
              )}
              {(tour.tags?.includes('Oferta') || (tour.original_price && tour.original_price > tour.price)) && (
                <Badge className="bg-[#FF6B4A] text-white text-xs font-semibold px-3 py-1.5 rounded-full border-0">
                  {t('tours_offer')}
                </Badge>
              )}
            </div>
            
            {/* Price Badge - Bottom */}
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <div>
                <h3 className="font-serif text-xl md:text-2xl font-medium text-white mb-1 line-clamp-2 drop-shadow-lg">
                  {tour.name}
                </h3>
                <div className="flex items-center gap-3 text-white/80 text-sm">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {tour.duration}
                  </span>
                  {tour.spots_available && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {tour.spots_available}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="glass rounded-2xl px-4 py-2 text-right">
                {tour.original_price && tour.original_price > tour.price && (
                  <span className="text-xs text-[#FF6B4A] line-through block">${tour.original_price}</span>
                )}
                <span className="text-2xl font-bold text-[#012A36]">${tour.price}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-[#4A6B74] text-sm mb-5 line-clamp-2 leading-relaxed">
              {tour.short_description}
            </p>

            {/* CTA Button */}
            <motion.button 
              className="w-full coral-btn py-4 text-base"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid={`tour-card-reserve-${tour.slug}`}
            >
              {t('tours_reserve')}
            </motion.button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default TourCard;
